import React, { FC, useEffect, useMemo, useState } from "react";
import { Props, OpenMeteoCurrentWeather, defaultData } from "./types";
import { getWeatherVisual } from "./weatherCodes";
import WeatherIcon from "./WeatherIcon";
import { resolveCurrentLocation } from "./location";
import "./Weather.sass";

const Weather: FC<Props> = ({ data = defaultData, loader, setData }) => {
  const [weatherData, setWeatherData] = useState<OpenMeteoCurrentWeather>();
  const [hasError, setHasError] = useState(false);
  const { push, pop } = loader;

  const hasLegacyCity = typeof data.city === "string" && data.city.length > 0;
  const location = data.location;
  const autoLocateAttempted = data.autoLocateAttempted ?? false;
  const hasPlaceholderLocation =
    location?.name === "Current location" &&
    (location.country === "Current location" || !location.country);

  useEffect(() => {
    if (!hasPlaceholderLocation || !location) {
      return;
    }

    let active = true;

    resolveCurrentLocation()
      .then((currentLocation) => {
        if (!active) return;
        setData({
          ...data,
          location: currentLocation,
          autoLocateAttempted: true,
          units: data.units ?? "metric",
          style: data.style ?? "cinematic",
        });
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [data, hasPlaceholderLocation, location, setData]);

  useEffect(() => {
    if (location || hasLegacyCity || autoLocateAttempted || !navigator.geolocation) {
      return;
    }

    let active = true;

    const autoLocate = async () => {
      try {
        const currentLocation = await resolveCurrentLocation();
        if (!active) return;
        setData({
          ...data,
          location: currentLocation,
          autoLocateAttempted: true,
          units: data.units ?? "metric",
          style: data.style ?? "cinematic",
        });
      } catch {
        if (!active) return;
        setData({
          ...data,
          autoLocateAttempted: true,
          units: data.units ?? "metric",
          style: data.style ?? "cinematic",
        });
      }
    };

    autoLocate();
    return () => {
      active = false;
    };
  }, [autoLocateAttempted, data, hasLegacyCity, location, setData]);

  useEffect(() => {
    if (!location) {
      setWeatherData(undefined);
      setHasError(false);
      return;
    }

    const controller = new AbortController();
    const loadWeather = async () => {
      push();
      setHasError(false);
      try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${location.latitude}&longitude=${location.longitude}&current=temperature_2m,apparent_temperature,weather_code,is_day,wind_speed_10m&timezone=auto`;
        const response = await fetch(url, { signal: controller.signal });
        const body = await response.json();
        setWeatherData(body.current);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setHasError(true);
        }
      } finally {
        pop();
      }
    };

    loadWeather();
    return () => controller.abort();
  }, [location?.latitude, location?.longitude, pop, push]);

  const visual = useMemo(() => {
    if (!weatherData) return null;
    return getWeatherVisual(weatherData.weather_code, weatherData.is_day === 1);
  }, [weatherData]);

  if (hasLegacyCity) {
    return (
      <div className="Weather weather-card__empty">
        Select a location
        <small>Saved weather cities from the old provider need to be selected again.</small>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="Weather weather-card__empty">
        {autoLocateAttempted ? "Select a location" : "Locating weather..."}
        <small>
          {autoLocateAttempted
            ? "Search any city worldwide to enable weather."
            : "Trying to initialize weather from your current location."}
        </small>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="Weather weather-card__empty">
        Weather unavailable
        <small>Please try again in a moment.</small>
      </div>
    );
  }

  if (!weatherData || !visual) {
    return null;
  }

  const locationLabel = location.name || [location.name, location.countryCode || location.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="Weather weather-card">
      <div className="weather-card__top">
        <div className="weather-card__icon">
          <WeatherIcon kind={visual.icon} />
        </div>
        <div className="weather-card__temp">{Math.round(weatherData.temperature_2m)}°</div>
      </div>
      <div className="weather-card__location">{locationLabel}</div>
    </div>
  );
};

export default Weather;
